import type { PositioningPhaseView } from '~/helpers/learner/positioning'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog'
import RatingField from '~/pages/scorm/components/survey/RatingField'

const MAX_NEEDS = 2000

type IProps = {
  phase: PositioningPhaseView
  objectives: readonly string[]
  busy: boolean
  onCancel: () => void
  onSubmit: (levels: number[], needs: string) => void
}

/**
 * L'auto-évaluation de l'apprenant (#321) : un niveau de 1 à 5 sur chaque
 * objectif du parcours, et ses attentes à l'entrée. Chaque objectif doit être
 * noté : un positionnement troué ne dirait rien du point de départ.
 */
function PositioningDialog({ phase, objectives, busy, onCancel, onSubmit }: IProps) {
  const { t } = useTranslation()
  const [levels, setLevels] = useState<(number | null)[]>(() => objectives.map(() => null))
  const [needs, setNeeds] = useState('')
  const complete = levels.every(level => level != null)
  const key = phase === 'ENTRY' ? 'entry' : 'exit'

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (complete) onSubmit(levels as number[], needs)
  }

  return (
    <Dialog open onOpenChange={open => !open && onCancel()}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-xl">
        <form onSubmit={submit} className="flex flex-col gap-5">
          <DialogHeader>
            <DialogTitle>{t(`positioning.${key}.title`)}</DialogTitle>
            <DialogDescription>{t('positioning.dialog.description')}</DialogDescription>
          </DialogHeader>
          {objectives.map((objective, index) => (
            <RatingField
              key={objective}
              legend={objective}
              value={levels[index] ?? null}
              onChange={level => setLevels(current => current.map((l, i) => (i === index ? level : l)))}
            />
          ))}
          {phase === 'ENTRY' && (
            <div className="flex flex-col gap-1.5">
              <label htmlFor="positioning-needs" className="text-sm font-medium">{t('positioning.dialog.needs')}</label>
              <textarea id="positioning-needs" rows={3} maxLength={MAX_NEEDS} value={needs} onChange={e => setNeeds(e.target.value)} className="rounded-md border border-input bg-transparent px-3 py-2 text-sm" />
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel}>{t('positioning.dialog.later')}</Button>
            <Button type="submit" disabled={!complete || busy}>{t('positioning.dialog.submit')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default PositioningDialog
